const { setBooleanAttribute, getBooleanAttribute } = require('../../../utilities/dom/booleanAttribute');
const { isString } = require('../../../utilities/type');
const getAnalyticsConfig = require('../../../common/getAnalyticsConfig');
const pfo = require('../models/productFieldObject');
const impressionsStore = require('../stores/impressionsStore');
const { queryProductImpressionElementFromDescendant } = require('./queryElement');

// JSDoc requires
const ifo = require('../models/impressionFieldObject'); // eslint-disable-line no-unused-vars

const analyticsConfig = getAnalyticsConfig();

/* -------------------------------------------------------------------------- *\
 * MUST READ
 * ----------------------------------------
 * This file contains impression related helpers that MUST be outside
 * 'impression' directory for two reasons:
 *
 *  1. Avoid a require loop
 *  2. They are used by function outside 'impressions' context
\* -------------------------------------------------------------------------- */

/**
 * Get the impression ID given a product impression element or a descendant.
 * This UID can be used to lookup in the impressions store.
 *
 * @param {Element} element Product impression element or descendant
 * @returns {string|null} Impression ID
 */
const getProductImpressionIdFromDOM = (element) => {
    const productImpressionElement = queryProductImpressionElementFromDescendant(element);
    const productImpressionId = productImpressionElement?.getAttribute(
        analyticsConfig.dataAttributes.productImpressionId
    );
    return productImpressionId ?? null;
};

/**
 * Get the impression data from the impressions store given a product impression
 * element or a descendant
 *
 * IMPORTANT NOTE:
 * Almost a copy of another 'getStoredImpressionFieldObjectFromDOM' from
 * 'datalayer/impressions/dom.js'.
 * In order to avoid a require loop we cannot import that function here.
 *
 * @async
 * @param {Element} element Product impression element or descendant
 * @returns {Promise<ifo.ImpressionFieldObject|null>} Datalayer impression
 */
const getStoredImpressionFieldObjectFromDOM = (element) => {
    const productImpressionElement = queryProductImpressionElementFromDescendant(element);
    const productImpressionId = productImpressionElement.getAttribute(
        analyticsConfig.dataAttributes.productImpressionId
    );
    return impressionsStore.getItem(productImpressionId);
};

/**
 * Get the product field object derived from a product impression element or a
 * descendant.
 *
 * @async
 * @param {Element} element Product impression element or descendant
 * @returns {Promise<pfo.ProductFieldObject|null>} The ProductFieldObject for the product
 * impression
 */
const getProductFieldObjectFromDOM = (element) => {
    return getStoredImpressionFieldObjectFromDOM(element).then(
        impressionFieldObject => impressionFieldObject !== null
            ? pfo.createProductFieldObject(
                impressionFieldObject.toDTO()
            )
            : null
    );
};

/**
 * Get the adDatalayerProductId by looking into impression store given a product
 * impression element or descendant
 *
 * @async
 * @param {Element} element Product impression element or descendant
 * @returns {Promise<string>} adDatalayerProductId
 */
const getAdDatalayerProductIdFromDOM = (element) => {
    return getStoredImpressionFieldObjectFromDOM(element)
        .then(
            (impressionFieldObject) => {
                if (impressionFieldObject === null) {
                    return Promise.reject(
                        new Error('Impression not found from the given element')
                    );
                }
                return impressionFieldObject.addons.adDatalayerProductId;
            }
        );
};

/**
 * Mark the script tag with the base JSON of an ImpressionFieldObject
 * as PROCESSED, which means that base IFO (static data with neither
 * list nor position) has been completed with the dynamic data and sent
 * to Impression store.
 *
 * @param {string|Element} target The impression ID or the root element
 *  of the product impression
 */
const markBaseImpressionFieldObjectAsProcessed = (target) => {
    const productImpressionId = isString(target)
        ? target
        : getProductImpressionIdFromDOM(target);
    const script = document.getElementById(productImpressionId);
    if (script) {
        setBooleanAttribute(
            script,
            analyticsConfig.dataAttributes.baseProductImpressionProcessed,
            true
        );
    }
};

/**
 * Check if the script tag with the base JSON of an ImpressionFieldObject
 * is PROCESSED, which means that base IFO (static data with neither
 * list nor position) has been completed with the dynamic data and sent
 * to Impression store.
 *
 * @param {stringtring|Element} target The impression ID or the root element
 *  of the product impression
 * @returns {boolean} True if the script has already been processed
 */
const isBaseImpressionFieldObjectProcessed = (target) => {
    let result = false;
    const productImpressionId = isString(target)
        ? target
        : getProductImpressionIdFromDOM(target);
    const script = document.getElementById(productImpressionId);
    if (script) {
        result = getBooleanAttribute(
            script,
            analyticsConfig.dataAttributes.baseProductImpressionProcessed
        );
    }
    return result;
};

/**
 * Modify an element to work as a duplicate of a product impression element.
 *
 * @param {Element} element Product impression element or descendant
 * @param {string} productImpressionId The impression ID used to lookup in
 *  impressions store
 */
const markAsProductImpressionElement = (element, productImpressionId) => {
    element.setAttribute(
        analyticsConfig.dataAttributes.productImpressionId,
        productImpressionId
    );
    let isImpressionProcessed = isBaseImpressionFieldObjectProcessed(productImpressionId);
    if (isImpressionProcessed) {
        setBooleanAttribute(
            element,
            analyticsConfig.dataAttributes.productImpressionCached,
            true
        );
    }
};

module.exports = {
    getAdDatalayerProductIdFromDOM,
    getProductImpressionIdFromDOM,
    getStoredImpressionFieldObjectFromDOM,
    getProductFieldObjectFromDOM,
    markAsProductImpressionElement,
    markBaseImpressionFieldObjectAsProcessed
};
